import { mapGetters } from "vuex"
import moment from "moment-timezone"
import { ElMessageBox } from "element-plus"
import userConstants from "@/config/constants/userConstants"

// Lab-order codes that must not be orderable from the colony labs
// (DLW-TX / DLW-HOU). For the parent DLW lab only DLW_UIBC is restricted.
const COLONY_RESTRICTED_LAB_ORDER_CODES = [
  "DLW_C_PEPTIDE",
  "DLW_FOB",
  "DLW_H_PYLORI_BREATH_TEST",
  "DLW_HSV1",
  "DLW_HSV2",
  "DLW_RET",
  "DLW_C3",
  "DLW_C4",
  "DLW_CA_125",
  "DLW_CA_15-3",
  "DLW_CA_19-9",
  // "DLW_UIBC",
  "DLW_HERPES_SIMPLEX_VIRUS",
  "DLW_HSV12",
]
const DLW_RESTRICTED_LAB_ORDER_CODES = ["DLW_UIBC"]

export default {
  computed: {
    ...mapGetters("profile", ["getProfileData"]),
    ...mapGetters("auth", ["getAuthenticatedUser"]),
    ...mapGetters("auth", ["getUserAllowedLabs", "getUserPrimaryLabId"]),
    isBillingUser() {
      const billingUserTypes = [
        "AR_LEAD",
        "AR_EXECUTIVE",
        "CP_LEAD",
        "CODING_LEAD",
        "CODING_EXECUTIVE",
        "POSTING_EXECUTIVE",
        "SR_FTE",
        "BILLING_REPRESENTATIVE",
      ]
      if (
        this.getAuthenticatedUser &&
        billingUserTypes.includes(this.getAuthenticatedUser.user_type)
      ) {
        return false
      }
      return true
    },
    isSuperAdmin() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type &&
        this.getAuthenticatedUser.user_type == "LAB_SUPER_ADMIN"
      ) {
        return true
      }
      return false
    },
    isFacilityUser() {
      if (this.getAuthenticatedUser && this.getAuthenticatedUser.user_type) {
        let users = [
          "BILLING_REPRESENTATIVE",
          "AR_LEAD",
          "AR_EXECUTIVE",
          "CP_LEAD",
          "CODING_LEAD",
          "CODING_EXECUTIVE",
          "POSTING_EXECUTIVE",
          "SR_FTE",
        ]
        if (users.indexOf(this.getAuthenticatedUser.user_type) !== -1) {
          return true
        } else {
          return false
        }
      }
      return false
    },
    isLabAssistantUser() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type &&
        (this.getAuthenticatedUser.user_type == "LAB_DATA_ASSISTANT" ||
          this.getAuthenticatedUser.user_type == "LAB_DATA_ADMIN")
      ) {
        return true
      }
      return false
    },
    isLabTechnologistUser() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type &&
        this.getAuthenticatedUser.user_type == "LAB_TECHNOLOGIST"
      ) {
        return true
      }
      return false
    },
    isOrderingPhysician() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type == "HOSPITAL_PHYSICIAN"
      ) {
        return true
      }
      return false
    },
    isUserLab() {
      if (this.getAuthenticatedUser && this.getAuthenticatedUser.user_type) {
        let users = [
          "LAB_ADMIN",
          "LAB_MANAGER",
          "LAB_DATA_ASSISTANT",
          "LAB_SUPER_ADMIN",
          "LAB_TECHNOLOGIST",
          "LAB_DATA_ADMIN",
        ]
        if (users.indexOf(this.getAuthenticatedUser.user_type) !== -1) {
          return true
        } else {
          return false
        }
      }
    },
    ableToReleaseReport() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type &&
        (this.getAuthenticatedUser.user_type == "LAB_MANAGER" ||
          this.getAuthenticatedUser.user_type == "LAB_ADMIN" ||
          this.getAuthenticatedUser.user_type == "LAB_TECHNOLOGIST" ||
          this.getAuthenticatedUser.user_type == "LAB_SUPER_ADMIN"
        )
       ) {
        return true
      }
      return false
    },
    checkDLWLab() {
      if (
        this.getProfileData &&
        this.getProfileData.lab &&
        this.getProfileData.lab.lab_code === "DLW"
      ) {
        return true
      }
      return false
    },
    isLabAdmin() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type &&
        this.getAuthenticatedUser.user_type == "LAB_ADMIN"
      ) {
        return true
      }
      return false
    },
    checkColonyLab() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        const matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        if (matchedLab && (matchedLab.lab_code == "DLW-TX" || matchedLab.lab_code == "DLW-HOU" || matchedLab.lab_code == "DLW")) {
          return true
        }
        return false
      }
    },
    // Logged-in user's current (primary) lab is TestGo Irving.
    isIrvingLab() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        const matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        return !!(matchedLab && matchedLab.lab_code == "DLW-TX")
      }
      return false
    },
    // Logged-in user's current (primary) lab is TestGo Houston.
    isHoustonLab() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        const matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        return !!(matchedLab && matchedLab.lab_code == "DLW-HOU")
      }
      return false
    },
    // Logged-in user's current (primary) lab is TestGo Homewood (DLW).
    isHomewoodLab() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        const matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        return !!(matchedLab && matchedLab.lab_code == "DLW")
      }
      return false
    },
    isChantillyLab() {
      const chantillyUsers = ["kedarm", "nkonda", "rohithm", "jvillalta","srilabtech"]
      if (
        this.getAuthenticatedUser &&
        chantillyUsers.includes(this.getAuthenticatedUser.username)
      ) {
        return false
      }
      return false
    },
    isLabUser() {
      if (this.getAuthenticatedUser && this.getAuthenticatedUser.user_type) {
        let users = [
          "LAB_ADMIN",
          "LAB_MANAGER",
          "BILLING_REPRESENTATIVE",
          "LAB_DATA_ASSISTANT",
          "LAB_SUPER_ADMIN",
          "LAB_DATA_ADMIN",
        ]
        if (users.indexOf(this.getAuthenticatedUser.user_type) !== -1) {
          return true
        } else {
          return false
        }
      }
      return false
    },
    isLabAbleToViewInventory() {
      if (this.getAuthenticatedUser && this.getAuthenticatedUser.user_type) {
        let users = [
          "LAB_ADMIN",
          "LAB_MANAGER",
          "BILLING_REPRESENTATIVE",
          "LAB_DATA_ASSISTANT",
          "LAB_SUPER_ADMIN",
          "LAB_DATA_ADMIN",
          "LAB_TECHNOLOGIST",
        ]
        if (users.indexOf(this.getAuthenticatedUser.user_type) !== -1) {
          return true
        } else {
          return false
        }
      }
      return false
    },
    isDLWLab() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        const matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        if (matchedLab && matchedLab.lab_code == "DLW") {
          return true
        }
        return false
      }
    },
    // Logged-in user's current (primary) lab is APEX.
    isAPEXLab() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        const matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        return !!(matchedLab && matchedLab.lab_code == "APX")
      }
      return false
    },
    // Set of lab-order codes that should be non-selectable when creating a
    // case / accession for the current lab. Colony (DLW-TX) and Houston
    // (DLW-HOU) block the full list; Homewood (DLW) blocks only DLW_UIBC.
    disabledLabOrderCodes() {
      if (this.isIrvingLab || this.isHoustonLab) {
        return new Set(COLONY_RESTRICTED_LAB_ORDER_CODES)
      }
      if (this.isHomewoodLab) {
        return new Set(DLW_RESTRICTED_LAB_ORDER_CODES)
      }
      return new Set()
    },
    isReleasePending() {
      if (this.getAuthenticatedUser && this.getAuthenticatedUser.user_type) {
        let users = [
          "HOSPITAL_ADMIN",
          "HOSPITAL_ACCOUNT_MANAGER",
          "HOSPITAL_PHYSICIAN",
          "MARKETER",
          // "LAB_SUPER_ADMIN"
        ]
        if (users.indexOf(this.getAuthenticatedUser.user_type) !== -1) {
          return true
        } else {
          return false
        }
      }
      return false
    },
    isAccountManager() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type &&
        this.getAuthenticatedUser.user_type == "ACCOUNT_MANAGER"
      ) {
        return true
      }
      return false
    },
    isAbletoAddInsurance() {
      let user = this.getAuthenticatedUser
      if (
        user &&
        user.username &&
        userConstants.ABLE_TO_ADD_INSURANCE_USERS.includes(user.username)
      ) {
        return true
      }
      return false
    },
    isAbletoGeneratePathologyReport() {
      let user = this.getAuthenticatedUser
      if (
        user &&
        user.username &&
        ["pathologist", "robert john"].includes(user.username)
      ) {
        return true
      }
      return false
    },
    isAbleToGenerateResendEMR() {
      let user = this.getAuthenticatedUser
      if (
        user &&
        user.username &&
        ["resendemr", "discreteresults", "labmanagertech"].includes(
          user.username,
        )
      ) {
        return true
      }
      return false
    },
    isGowthamUser() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.username === "colony"
      ) {
        return true
      } else {
        return false
      }
    },
    isDLWUser() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.username === "charan.dlw"
      ) {
        return true
      } else {
        return false
      }
    },
    isAbleToEditPhlebio() {
      let user = this.getAuthenticatedUser
      if (
        user &&
        user.username &&
        ["gowtham.dlw", "akhilas", "harshann", "srikanth"].includes(
          user.username,
        )
      ) {
        return true
      }
      return false
    },
    isAbletoVerifyInsuranceDiscovery() {
      let user = this.getAuthenticatedUser
      if (
        user &&
        user.username &&
        userConstants.ABLE_TO_VERIFY_INSURANCE_DISCOVERY_USERS.includes(user.username)
      ) {
        return true
      }
      return false
    },
    checkForLabTimeZone() {
      if (this.getUserAllowedLabs && this.getUserPrimaryLabId) {
        let matchedLab = this.getUserAllowedLabs.find(
          (lab) => lab._id === this.getUserPrimaryLabId,
        )
        if (matchedLab) {
          return matchedLab?.timezone
        }
      }
    }
  },
  methods: {
    changetocaptila(str) {
      var splitStr = str.toLowerCase().split(" ")
      for (var i = 0; i < splitStr.length; i++) {
        splitStr[i] =
          splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
      }
      return splitStr.join(" ")
    },
    getSelectedCaseTypesGlobal(data) {
      return data.reduce((acc, current) => {
        const exists = acc.find(
          (item) => item.case_type_code == current.case_type_code,
        )
        if (!exists) {
          acc.push(current)
        }
        return acc
      }, [])
    },
    convertToUppercase(str) {
      return str ? str.toUpperCase() : "--"
    },
    allowAlphaNumericSpace(e) {
      var code = "charCode" in e ? e.charCode : e.keyCode
      if (
        !(code == 32) && // space
        !(code > 47 && code < 58) && // numeric (0-9)
        !(code > 64 && code < 91) && // upper alpha (A-Z)
        !(code > 96 && code < 123)
      ) {
        // lower alpha (a-z)
        e.preventDefault()
      }
    },
    isLetter(e) {
      let char = String.fromCharCode(e.keyCode) // Get the character
      if (/^[A-Za-z\s]+$/.test(char)) return true // Match with regex
      else e.preventDefault() // If not match, don't add to input text
    },
    onlyNumber($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which
      if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) {
        // 46 is dot
        $event.preventDefault()
      }
    },
    isInactiveStatus(data) {
      return data.status && data.status == "INACTIVE" ? true : false
    },
    dateWithMomentUTC(value, format = "MM-DD-YYYY") {
      if (!value) return
      return moment(value).utc().format(format)
    },
    momentWithTimezone: function (value, format = "MM-DD-YYYY") {
      if (!value) return ""
      let timeZone =
        this.checkForLabTimeZone ? this.checkForLabTimeZone : "America/New_York"
      return moment(value).tz(timeZone).format(format)
    },
    momentWithTimezoneTime: function (value, format = "MM-DD-YYYY hh:mm A") {
      if (!value) return ""
      let timeZone =
        this.checkForLabTimeZone ? this.checkForLabTimeZone : "America/New_York"
      return moment(value).tz(timeZone).format(format)
    },
    sortByKey(data, key = "test_name") {
      if (data && data.length) {
        data.sort((a, b) => {
          const nameA = a[key].toUpperCase()
          const nameB = b[key].toUpperCase()

          if (nameA > nameB) {
            return 1
          } else if (nameA < nameB) {
            return -1
          }
          return 0
        })
      }
      return data
    },
    getStateNameByCode(code) {
      let stateByCode = {
        AL: "Alabama",
        AK: "Alaska",
        AS: "American Samoa",
        AZ: "Arizona",
        AR: "Arkansas",
        CA: "California",
        CO: "Colorado",
        CT: "Connecticut",
        DE: "Delaware",
        DC: "District Of Columbia",
        FM: "Federated States Of Micronesia",
        FL: "Florida",
        GA: "Georgia",
        GU: "Guam",
        HI: "Hawaii",
        ID: "Idaho",
        IL: "Illinois",
        IN: "Indiana",
        IA: "Iowa",
        KS: "Kansas",
        KY: "Kentucky",
        LA: "Louisiana",
        ME: "Maine",
        MH: "Marshall Islands",
        MD: "Maryland",
        MA: "Massachusetts",
        MI: "Michigan",
        MN: "Minnesota",
        MS: "Mississippi",
        MO: "Missouri",
        MT: "Montana",
        NE: "Nebraska",
        NV: "Nevada",
        NH: "New Hampshire",
        NJ: "New Jersey",
        NM: "New Mexico",
        NY: "New York",
        NC: "North Carolina",
        ND: "North Dakota",
        MP: "Northern Mariana Islands",
        OH: "Ohio",
        OK: "Oklahoma",
        OR: "Oregon",
        PW: "Palau",
        PA: "Pennsylvania",
        PR: "Puerto Rico",
        RI: "Rhode Island",
        SC: "South Carolina",
        SD: "South Dakota",
        TN: "Tennessee",
        TX: "Texas",
        UT: "Utah",
        VT: "Vermont",
        VI: "Virgin Islands",
        VA: "Virginia",
        WA: "Washington",
        WV: "West Virginia",
        WI: "Wisconsin",
        WY: "Wyoming",
      }
      let stateName = ""
      Object.keys(stateByCode).map((key) => {
        if (key == code) {
          stateName = stateByCode[key].toUpperCase()
        }
      })
      return stateName
    },
    convertToLabUTC(dateTime, end_date = false) {
      if (!dateTime) return
      let clientTz = moment.tz.guess()

      let labTimezone = this.checkForLabTimeZone ? this.checkForLabTimeZone : "America/New_York"
      let clientTime = moment(dateTime).tz(clientTz)

      let h = clientTime.hour()
      let mn = clientTime.minutes()
      let s = clientTime.seconds()

      let y = clientTime.year()
      let d = clientTime.date()
      let m = clientTime.month()
      if (end_date) {
        return moment()
          .tz(labTimezone)
          .set({
            hours: h,
            minutes: mn,
            year: y,
            seconds: s,
            date: d,
            month: m,
          })
          .utc()
          .endOf("day")
          .format()
      } else {
        return moment()
          .tz(labTimezone)
          .set({
            hours: h,
            minutes: mn,
            year: y,
            seconds: s,
            date: d,
            month: m,
          })
          .utc()
          .format()
      }
    },
    getLabTimeZoneData(dateTime) {
      let clientTz = moment.tz.guess()

      let clientTime = moment(dateTime).tz(clientTz)

      let h = clientTime.hour()
      let mn = clientTime.minutes()
      let s = clientTime.seconds()

      let y = clientTime.year()
      let d = clientTime.date()
      let m = clientTime.month()

      return {
        hours: h,
        minutes: mn,
        year: y,
        seconds: s,
        date: d,
        month: m,
      }
    },
    convertLabUTCToClient(dateTime) {
      let labTimezone = this.checkForLabTimeZone ? this.checkForLabTimeZone : "America/New_York"
      return new Date(dateTime).toLocaleString("en-us", {
        timeZone: labTimezone,
      })
    },
    getPatientNameByInfo(patientInfo) {
      if (!patientInfo) return
      let name = []
      if (patientInfo.first_name) {
        name.push(patientInfo.first_name)
      }
      if (patientInfo.middle_name) {
        name.push(patientInfo.middle_name)
      }
      if (patientInfo.last_name) {
        name.push(patientInfo.last_name)
      }
      return name.join(" ")
    },
    copyTitle(titleData) {
      const title = titleData
      navigator.clipboard
        .writeText(title)
        .then(() => {
          console.log("Title copied to clipboard:", title)
        })
        .catch((error) => {
          console.error("Failed to copy title to clipboard:", error)
        })
    },
    formatDate(date, format = "MM-DD-YYYY") {
      if (date) {
        return moment.utc(date).format(format)
      } else {
        return "--"
      }
    },
    scrollToFirstError() {
      let el = this.$el.getElementsByClassName("err")[0]

      if (el) {
        el.scrollIntoView({ block: "center" })
      }
    },
    isFinalizedAmendmentCase(caseDetails) {
      return !!(
        caseDetails &&
        caseDetails.initial_finalized_date &&
        caseDetails.is_amendement === true &&
        this.getAuthenticatedUser?.username !== "kalyantech" && this.getAuthenticatedUser?.role !== "LAB_TECHNOLOGIST"
      )
    },
  },

}
